import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { supabaseApi } from '~/supabase/api'
import { ModelV2 } from '~/supabase/types'
import { map, pipe, filter, isNonNullish, partition } from 'remeda'
import { GET_INVENTORY_QUERY_KEY } from '~/features/hooks/useGetInventory'
import { GET_SHOPIFY_PRODUCTS_QUERY_KEY } from '~/features/shopify/hooks/useGetShopifyProducts'

export const usePatchModels = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['patch-models'],
    mutationFn: async (payload: Array<Partial<ModelV2> & { id: number }>) => {
      const updated_at = new Date().toISOString()
      const promises = await Promise.all(
        payload.map((p) =>
          supabaseApi
            .from('models_v2')
            .update({ ...p, updated_at })
            .eq('id', p.id)
            .select()
        )
      )

      const errors = pipe(
        promises,
        map((p) => p.error),
        filter(isNonNullish)
      )

      errors.forEach((error) => {
        if (error) {
          toast.error(error.message)
        }
      })
      if (errors.length > 0) {
        throw new Error('Model update failed')
      }
      const updatedModels = promises.map((p) => p.data)
      const [failedModels, successfulModels] = partition(updatedModels, (p) => p === null)

      if (failedModels.length > 0) {
        const message =
          successfulModels.length > 0
            ? 'Some models were not updated. You might not have permission.'
            : 'Model update failed'
        toast.error(message)
      }
    },
    onSuccess: () => {
      toast.success('Model updates successful.')
      queryClient.invalidateQueries({ queryKey: [GET_INVENTORY_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [GET_SHOPIFY_PRODUCTS_QUERY_KEY] })
    },
  })
}
